import { createContext, useContext, type ReactNode } from 'react'
import { getLettersFromText } from '../lib/pairings'

type SearchContextValue = {
  letterSearch: string[]
  hasSearch: boolean
  searchPattern: string
  getMatchIndices: (text: string) => number[]
  getVerticalMatchIndices: (textA: string, textB: string) => number[]
}

const SearchContext = createContext<SearchContextValue | null>(null)

export function formatSearchPattern(letterSearch: string[]): string {
  const lastFilledIndex = letterSearch.reduce(
    (last, letter, index) => letter.trim() ? index : last,
    -1
  )
  if (lastFilledIndex < 0) return ''
  return letterSearch
    .slice(0, lastFilledIndex + 1)
    .map(letter => letter.trim() ? letter.toUpperCase() : '_')
    .join('')
}

export function SearchProvider({
  letterSearch,
  children
}: {
  letterSearch: string[]
  children: ReactNode
}) {
  const filledIndices = letterSearch.map((l, i) => l.trim() ? i : -1).filter(i => i >= 0)
  const searchLetters = letterSearch.filter(l => l.trim()).join('').toUpperCase()
  const hasSearch = filledIndices.length > 0
  const searchPattern = formatSearchPattern(letterSearch)

  // Determine search type
  const isPrefix = hasSearch && filledIndices.every((idx, i) => idx === i)

  const upperSearch = letterSearch.map(l => l.toUpperCase())

  const getMatchIndices = (text: string): number[] => {
    if (filledIndices.length === 0) return []

    const letters = getLettersFromText(text)

    if (isPrefix) {
      // Prefix search - word starts with the pattern
      if (letters.startsWith(searchLetters)) {
        return filledIndices
      }
      return []
    } else {
      // Positional search - exact position match
      const matches = filledIndices.every(idx => letters[idx] === upperSearch[idx])
      return matches ? filledIndices : []
    }
  }

  // Vertical search: find columns where stacked letters match the search
  // e.g., CAT/DOG with search "CD" matches column 0 (C on top, D on bottom)
  const getVerticalMatchIndices = (textA: string, textB: string): number[] => {
    if (searchLetters.length === 0) return []

    const lettersA = getLettersFromText(textA)
    const lettersB = getLettersFromText(textB)
    const minLen = Math.min(lettersA.length, lettersB.length)

    const matchingColumns: number[] = []

    // Check each column for a vertical match
    for (let col = 0; col <= minLen - searchLetters.length; col++) {
      // Build the vertical string at this column position
      // For a 2-letter search, we check if letterA[col] + letterB[col] matches
      // For longer searches, we'd need to handle differently, but pairs are always 2 rows
      if (searchLetters.length === 2) {
        const verticalPair = (lettersA[col] ?? '') + (lettersB[col] ?? '')
        if (verticalPair === searchLetters) {
          matchingColumns.push(col)
        }
      }
    }

    // Also check if search spans multiple columns vertically
    // e.g., "CDAO" could match columns 0-1 reading down then right
    // For now, just support simple 2-letter vertical column matches

    return matchingColumns
  }

  const value = { letterSearch, hasSearch, searchPattern, getMatchIndices, getVerticalMatchIndices }

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  )
}

export function useSearch() {
  return useContext(SearchContext)
}
